import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Clock, Save, Loader2 } from "lucide-react";

const formatTimeDisplay = (time) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm} ET`;
};

const ReminderSettingsCard = React.memo(function ReminderSettingsCard({
  todayReminderEnabled,
  setTodayReminderEnabled,
  finalReminderEnabled,
  setFinalReminderEnabled,
  todayReminderTime,
  setTodayReminderTime,
  finalReminderTime,
  setFinalReminderTime,
  savingReminders,
  handleSaveReminders,
}) {
  return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-600" />
            <CardTitle>Email Reminder Times</CardTitle>
          </div>
          <CardDescription>
            Configure when email reminders are sent to users who haven't submitted their logs.
            All times are in Eastern Time (ET) and reminders are sent on weekdays only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Today's Reminder */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="today-reminder-enabled"
                checked={todayReminderEnabled}
                onCheckedChange={setTodayReminderEnabled}
              />
              <Label htmlFor="today-reminder-enabled" className="text-sm font-medium cursor-pointer">
                Today's Log Reminder
              </Label>
            </div>
            <p className="text-sm text-slate-500 mb-2">
              Sent to users who haven't submitted today's log
            </p>
            {todayReminderEnabled && (
              <div className="flex items-center gap-4 ml-8">
                <input
                  id="today-reminder"
                  type="time"
                  value={todayReminderTime}
                  onChange={(e) => setTodayReminderTime(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-sm text-slate-500">
                  Currently: {formatTimeDisplay(todayReminderTime)}
                </span>
              </div>
            )}
          </div>

          {/* Final Reminder */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="final-reminder-enabled"
                checked={finalReminderEnabled}
                onCheckedChange={setFinalReminderEnabled}
              />
              <Label htmlFor="final-reminder-enabled" className="text-sm font-medium cursor-pointer">
                Final Reminder (Previous Day)
              </Label>
            </div>
            <p className="text-sm text-slate-500 mb-2">
              Final reminder for users who haven't submitted the previous day's log
            </p>
            {finalReminderEnabled && (
              <div className="flex items-center gap-4 ml-8">
                <input
                  id="final-reminder"
                  type="time"
                  value={finalReminderTime}
                  onChange={(e) => setFinalReminderTime(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-sm text-slate-500">
                  Currently: {formatTimeDisplay(finalReminderTime)}
                </span>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={handleSaveReminders} disabled={savingReminders}>
              {savingReminders ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Reminder Times
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
  );
});

export default ReminderSettingsCard;
